import { useCallback, useEffect, useRef, useState } from 'react'

import {
  generateMusic,
  getTaskStatus,
  prepareAdvancedVideoEditor,
  getAdvancedVideoEditorJobStatus,
  getAdvancedVideoEditorSession,
  getAdvancedVideoEditorExports
} from '../../../services/api/aiVideoGeneration'
import {
  getTimelineEditorUrl,
  withTimelineEditorRedirect
} from '../utils/timelineEditor'

const POLL_INTERVAL = 2500
const POLL_TIMEOUT = 20 * 60 * 1000
const SUCCESS_STATUSES = ['success', 'completed', 'done', 'ready']
const FAILURE_STATUSES = ['failed', 'error', 'cancelled']

const wait = duration =>
  new Promise(resolve => {
    setTimeout(resolve, duration)
  })

const getResponseData = response =>
  response?.data?.data || response?.data || response || {}

const normalizeProgress = value => {
  if (value === null || value === undefined || value === '') return null

  const numericValue =
    typeof value === 'string' ? Number(value.replace('%', '')) : Number(value)

  if (!Number.isFinite(numericValue)) return null
  return numericValue <= 1
    ? Math.round(numericValue * 100)
    : Math.round(numericValue)
}

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  fallback

const getLiveRedirectUrl = context =>
  (typeof window !== 'undefined' ? window.location.href : '') ||
  context?.redirectUrl ||
  ''

const pollTask = async ({
  getStatus,
  isCurrent,
  onProgress,
  timeout = POLL_TIMEOUT
}) => {
  const startedAt = Date.now()

  while (Date.now() - startedAt < timeout) {
    if (!isCurrent()) {
      throw new Error('Timeline editor preparation was cancelled')
    }

    const response = await getStatus()
    const data = getResponseData(response)
    const status = String(data.status || '').toLowerCase()
    const progress = normalizeProgress(data.progress)

    if (progress !== null) {
      onProgress(progress)
    }

    if (
      SUCCESS_STATUSES.includes(status) ||
      data.ready === true ||
      progress >= 100
    ) {
      return data
    }

    if (FAILURE_STATUSES.includes(status)) {
      throw new Error(
        data.error_msg ||
          data.error ||
          data.message ||
          'Timeline editor preparation failed'
      )
    }

    await wait(POLL_INTERVAL)
  }

  throw new Error('Timeline editor preparation timed out')
}

const useTimelineEditorFlow = ({
  buildPreparePayload,
  onProjectCreated,
  onAddExportToHistory
}) => {
  const operationIdRef = useRef(0)

  const [visible, setVisible] = useState(false)
  const [context, setContext] = useState(null)
  const [projectId, setProjectId] = useState(null)
  const [editorUrl, setEditorUrl] = useState(null)
  const [loading, setLoading] = useState(false)
  const [preparing, setPreparing] = useState(false)
  const [error, setError] = useState(null)
  const [eligibilityError, setEligibilityError] = useState(null)
  const [statusText, setStatusText] = useState('')
  const [progress, setProgress] = useState(0)

  const [exportsLoading, setExportsLoading] = useState(false)
  const [exportsError, setExportsError] = useState(null)
  const [exports, setExports] = useState([])

  const beginOperation = useCallback(() => {
    operationIdRef.current += 1
    return operationIdRef.current
  }, [])

  const isOperationCurrent = useCallback(
    operationId => operationIdRef.current === operationId,
    []
  )

  const loadExports = useCallback(async targetProjectId => {
    if (!targetProjectId) {
      setExports([])
      return
    }

    setExportsLoading(true)
    setExportsError(null)

    try {
      const response = await getAdvancedVideoEditorExports(targetProjectId)
      const data = getResponseData(response)
      setExports(Array.isArray(data.exports) ? data.exports : [])
    } catch (loadError) {
      setExportsError(
        getErrorMessage(loadError, 'Failed to load timeline exports')
      )
    } finally {
      setExportsLoading(false)
    }
  }, [])

  const loadExistingProject = useCallback(
    async nextContext => {
      const nextProjectId = nextContext?.projectId
      if (!nextProjectId) return

      const operationId = beginOperation()

      setProjectId(nextProjectId)
      setEditorUrl(null)
      setError(null)
      setLoading(true)
      setProgress(20)
      setStatusText('Restoring timeline session...')
      loadExports(nextProjectId)

      try {
        const response = await getAdvancedVideoEditorSession(nextProjectId)
        const session = getResponseData(response)

        if (!isOperationCurrent(operationId)) return

        const resolvedEditorUrl =
          session.editor_url || getTimelineEditorUrl(nextProjectId)

        if (!resolvedEditorUrl) {
          throw new Error('Timeline session did not include an editor URL')
        }

        setEditorUrl(
          withTimelineEditorRedirect(
            resolvedEditorUrl,
            getLiveRedirectUrl(nextContext)
          )
        )
        setProgress(100)
        setStatusText('Timeline ready')
      } catch (loadError) {
        if (!isOperationCurrent(operationId)) return
        setError(
          getErrorMessage(loadError, 'Failed to restore the timeline editor')
        )
      } finally {
        if (isOperationCurrent(operationId)) {
          setLoading(false)
        }
      }
    },
    [beginOperation, isOperationCurrent, loadExports]
  )

  const openEditor = useCallback(
    nextContext => {
      setVisible(true)
      setContext(nextContext)
      setExports([])
      setExportsError(null)
      setError(null)
      setEligibilityError(null)
      setProgress(0)
      setStatusText('')

      if (nextContext?.projectId) {
        loadExistingProject(nextContext)
        return
      }

      beginOperation()
      setProjectId(null)
      setEditorUrl(null)
      setLoading(Boolean(nextContext?.loading))
      setPreparing(false)
    },
    [beginOperation, loadExistingProject]
  )

  const startNewProject = useCallback(
    async ({ includeMusic = false, userPrompt = '', musicVolume = 0.3 }) => {
      if (!context || preparing) return

      const operationId = beginOperation()
      const isCurrent = () => isOperationCurrent(operationId)

      setLoading(false)
      setPreparing(true)
      setError(null)
      setProgress(5)
      setStatusText('Building timeline payload...')

      try {
        const preparedResult = await buildPreparePayload(context)
        const payload = preparedResult?.payload || preparedResult
        const sourceData = preparedResult?.sourceData || null

        if (!payload?.clustered_shooting_list) {
          throw new Error('Timeline payload is missing the shooting list')
        }

        let musicUrl = null

        if (includeMusic) {
          setStatusText('Generating background music...')
          setProgress(10)

          const musicResponse = await generateMusic({
            mode: 'shootinglist',
            shooting_list: payload.clustered_shooting_list,
            user_prompt: userPrompt,
            music_volume: musicVolume,
            video_id: context.videoId
          })
          const musicTaskId = getResponseData(musicResponse).task_id

          if (!musicTaskId) {
            throw new Error('Music generation did not return a task ID')
          }

          const musicResult = await pollTask({
            getStatus: () => getTaskStatus(musicTaskId),
            isCurrent,
            onProgress: nextProgress => {
              setProgress(10 + Math.round(nextProgress * 0.35))
            }
          })

          musicUrl = musicResult?.output?.full_music_s3_url

          if (!musicUrl) {
            throw new Error('Music generation completed without an audio URL')
          }
        }

        if (!isCurrent()) return

        const redirectUrl = getLiveRedirectUrl(context)

        setStatusText('Preparing editable timeline...')
        setProgress(includeMusic ? 50 : 20)

        const prepareResponse = await prepareAdvancedVideoEditor({
          ...payload,
          name: context.projectName || 'AI Video Timeline',
          redirect_url: redirectUrl,
          ...(musicUrl
            ? {
                music_url: musicUrl,
                music_volume: musicVolume
              }
            : {})
        })
        const prepareData = getResponseData(prepareResponse)

        if (!prepareData.project_id) {
          throw new Error('Timeline preparation did not return a project ID')
        }

        if (onProjectCreated) {
          await onProjectCreated({
            projectId: prepareData.project_id,
            context,
            sourceData,
            response: prepareData
          })
        }

        if (!isCurrent()) return

        setProjectId(prepareData.project_id)
        setContext(previousContext => ({
          ...previousContext,
          projectId: prepareData.project_id
        }))

        if (
          !prepareData.ready &&
          (prepareData.status_url || prepareData.job_id)
        ) {
          setStatusText('Uploading timeline media...')
          setProgress(includeMusic ? 55 : 30)

          await pollTask({
            getStatus: () =>
              getAdvancedVideoEditorJobStatus(
                prepareData.status_url || prepareData.job_id
              ),
            isCurrent,
            onProgress: nextProgress => {
              const baseProgress = includeMusic ? 55 : 30
              const remainingProgress = 100 - baseProgress
              setProgress(
                baseProgress +
                  Math.round((nextProgress / 100) * remainingProgress)
              )
            }
          })
        }

        if (!isCurrent()) return

        const resolvedEditorUrl = withTimelineEditorRedirect(
          prepareData.editor_url ||
            getTimelineEditorUrl(prepareData.project_id),
          redirectUrl
        )

        setEditorUrl(resolvedEditorUrl)
        setStatusText('Timeline ready')
        setProgress(100)
        loadExports(prepareData.project_id)

        if (typeof window !== 'undefined' && resolvedEditorUrl) {
          window.open(resolvedEditorUrl, '_blank', 'noopener,noreferrer')
        }
      } catch (prepareError) {
        if (!isCurrent()) return

        setError(
          getErrorMessage(prepareError, 'Failed to prepare the timeline editor')
        )
      } finally {
        if (isCurrent()) {
          setPreparing(false)
        }
      }
    },
    [
      beginOperation,
      buildPreparePayload,
      context,
      isOperationCurrent,
      loadExports,
      onProjectCreated,
      preparing
    ]
  )

  const continueToEditor = useCallback(
    options => {
      if (!projectId) {
        startNewProject(options)
        return
      }

      const resolvedEditorUrl = withTimelineEditorRedirect(
        editorUrl || getTimelineEditorUrl(projectId),
        getLiveRedirectUrl(context)
      )

      if (typeof window !== 'undefined' && resolvedEditorUrl) {
        window.open(resolvedEditorUrl, '_blank', 'noopener,noreferrer')
      }
    },
    [context, editorUrl, projectId, startNewProject]
  )

  const addExportToHistory = useCallback(
    timelineExport => {
      if (!onAddExportToHistory) return Promise.resolve(false)

      return onAddExportToHistory({
        exportItem: timelineExport,
        projectId,
        context
      })
    },
    [context, onAddExportToHistory, projectId]
  )

  const close = useCallback(() => {
    beginOperation()

    setVisible(false)
    setContext(null)
    setProjectId(null)
    setEditorUrl(null)
    setLoading(false)
    setPreparing(false)
    setError(null)
    setEligibilityError(null)
    setStatusText('')
    setProgress(0)
    setExports([])
    setExportsError(null)
  }, [beginOperation])

  const refreshExports = useCallback(() => {
    loadExports(projectId)
  }, [loadExports, projectId])

  useEffect(() => {
    if (!visible || !projectId || typeof window === 'undefined') {
      return undefined
    }

    const handleFocus = () => {
      loadExports(projectId)
    }

    window.addEventListener('focus', handleFocus)
    return () => window.removeEventListener('focus', handleFocus)
  }, [loadExports, projectId, visible])

  return {
    openEditor,
    setLoading,
    setEligibilityError,
    timelineModalProps: {
      visible,
      projectId,
      loading,
      preparing,
      error,
      eligibilityError,
      statusText,
      progress,
      exportsLoading,
      exportsError,
      exports,
      historyUrls: Array.isArray(context?.historyUrls)
        ? context.historyUrls
        : [],
      onClose: close,
      onRefresh: refreshExports,
      onContinue: continueToEditor,
      onAddToHistory: onAddExportToHistory ? addExportToHistory : null
    }
  }
}

export default useTimelineEditorFlow
